import type { SoftphoneDevicesHook } from './index.types';
import { AudioService } from 'Modules/softphone/services/AudioService';
import { DeviceService } from 'Modules/softphone/services/DeviceService';
import { EventService } from 'Modules/softphone/services/EventService';
import { WebRTCService } from 'Modules/softphone/services/WebRTCService';
import { useSoftphoneStore } from 'Modules/softphone/store';
import {
  getPersistedMicrophone,
  getPersistedRinger,
  getPersistedSpeaker,
  setPersistedMicrophone,
  setPersistedRinger,
  setPersistedSpeaker,
} from 'Modules/softphone/store/storage';
import { devicesHookLogger as logger } from 'Modules/softphone/utils/logger';
import { useCallback } from 'react';

export type { SoftphoneDevicesHook } from './index.types';

export const useSoftphoneDevices = (): SoftphoneDevicesHook => {
  const {
    microphones,
    speakers,
    ringers,
    selectedMicrophone,
    selectedSpeaker,
    selectedRinger,
    setSelectedMicrophone,
    setSelectedSpeaker,
    setSelectedRinger,
    setMicrophones,
    setSpeakers,
    setRingers,
    setPermissionState,
  } = useSoftphoneStore((state) => ({
    microphones: state.microphones,
    speakers: state.speakers,
    ringers: state.ringers,
    selectedMicrophone: state.selectedMicrophone,
    selectedSpeaker: state.selectedSpeaker,
    selectedRinger: state.selectedRinger,
    setSelectedMicrophone: state.setSelectedMicrophone,
    setSelectedSpeaker: state.setSelectedSpeaker,
    setSelectedRinger: state.setSelectedRinger,
    setMicrophones: state.setMicrophones,
    setSpeakers: state.setSpeakers,
    setRingers: state.setRingers,
    setPermissionState: state.setPermissionState,
  }));

  const eventService = EventService.getInstance();

  /**
   * Resolve a human-readable label for a device id from a device list.
   */
  const getDeviceLabel = (
    devices: MediaDeviceInfo[],
    deviceId: string | null
  ): string | null => {
    if (!deviceId) return null;
    return devices.find((d) => d.deviceId === deviceId)?.label || deviceId;
  };

  const audioService = AudioService.getInstance();
  const deviceService = DeviceService.getInstance();
  const webrtcService = WebRTCService.getInstance();

  /**
   * Select a microphone device
   * Persists to localforage for compatibility with legacy PhoneStore.
   * Applies to active calls via WebRTCService.
   * @param deviceId - Device ID to select
   */
  const selectMicrophone = useCallback(
    async (deviceId: string) => {
      logger.info('🎤 Selecting microphone', { deviceId });

      eventService.logDeviceChange(
        'microphone',
        getDeviceLabel(microphones, selectedMicrophone),
        getDeviceLabel(microphones, deviceId) || deviceId,
        { fromDeviceId: selectedMicrophone, toDeviceId: deviceId }
      );

      setSelectedMicrophone(deviceId);

      // Persist to localforage (shared with legacy PhoneStore)
      try {
        await setPersistedMicrophone(deviceId);
        logger.info('Microphone persisted to localforage');
      } catch (error) {
        logger.error(
          'Failed to persist microphone',
          error instanceof Error ? error : new Error(String(error))
        );
      }

      const activeCalls = useSoftphoneStore.getState().activeCalls;
      if (activeCalls.size > 0) {
        logger.info(`🔄 Applying microphone to ${activeCalls.size} active call(s)`);

        for (const [callId] of activeCalls) {
          try {
            await webrtcService.setMicrophone(callId, deviceId);
            logger.info(`✅ Microphone applied to call: ${callId}`);
          } catch (error) {
            logger.error(
              `Failed to apply microphone to call: ${callId}`,
              error instanceof Error ? error : new Error(String(error))
            );
          }
        }
      }
    },
    [eventService, microphones, selectedMicrophone, setSelectedMicrophone, webrtcService]
  );

  /**
   * Select a speaker device
   * Uses AudioService singleton to update all audio elements.
   * Persists to localforage for compatibility with legacy PhoneStore.
   * @param deviceId - Device ID to select
   */
  const selectSpeaker = useCallback(
    async (deviceId: string) => {
      logger.info('Selecting speaker', { deviceId });

      eventService.logDeviceChange(
        'speaker',
        getDeviceLabel(speakers, selectedSpeaker),
        getDeviceLabel(speakers, deviceId) || deviceId,
        { fromDeviceId: selectedSpeaker, toDeviceId: deviceId }
      );

      setSelectedSpeaker(deviceId);

      // Use AudioService to update all audio elements (single source of truth)
      try {
        await audioService.setSpeaker(deviceId);
        logger.info('Speaker applied via AudioService');
      } catch (error) {
        logger.error(
          'Failed to set speaker',
          error instanceof Error ? error : new Error(String(error))
        );
      }

      // Persist to localforage (shared with legacy PhoneStore)
      try {
        await setPersistedSpeaker(deviceId);
        logger.info('Speaker persisted to localforage');
      } catch (error) {
        logger.error(
          'Failed to persist speaker',
          error instanceof Error ? error : new Error(String(error))
        );
      }
    },
    [audioService, eventService, speakers, selectedSpeaker, setSelectedSpeaker]
  );

  /**
   * Select a ringer device
   * Uses AudioService singleton to update only the ring-alert audio elements
   * (ringtone, ringback, secondIncoming) — independent of the Speakers device.
   * Persists to localforage for compatibility with legacy PhoneStore.
   * @param deviceId - Device ID to select
   */
  const selectRinger = useCallback(
    async (deviceId: string) => {
      logger.info('Selecting ringer', { deviceId });

      eventService.logDeviceChange(
        'ringer',
        getDeviceLabel(ringers, selectedRinger),
        getDeviceLabel(ringers, deviceId) || deviceId,
        { fromDeviceId: selectedRinger, toDeviceId: deviceId }
      );

      setSelectedRinger(deviceId);

      try {
        await audioService.setRinger(deviceId);
        logger.info('Ringer applied via AudioService');
      } catch (error) {
        logger.error(
          'Failed to set ringer',
          error instanceof Error ? error : new Error(String(error))
        );
      }

      try {
        await setPersistedRinger(deviceId);
        logger.info('Ringer persisted to localforage');
      } catch (error) {
        logger.error(
          'Failed to persist ringer',
          error instanceof Error ? error : new Error(String(error))
        );
      }
    },
    [audioService, eventService, ringers, selectedRinger, setSelectedRinger]
  );

  /**
   * Refresh the list of available devices
   * Requests permissions first to ensure device labels are available
   * Also loads persisted device preferences (shared with legacy PhoneStore)
   */
  const refreshDevices = useCallback(async () => {
    logger.info('🔄 Starting device refresh');

    try {
      // Ids and labels stay hidden until a stream has been opened once, so
      // enumerate first and prompt only when the permission is undecided or
      // the result is still unusable.
      let permission = await deviceService.getPermissionState();
      let microphoneDevices = await deviceService.getAudioInputDevices();

      if (permission !== 'granted' || microphoneDevices.length === 0) {
        logger.info('🎤 Requesting microphone permissions for device enumeration');
        permission = await deviceService.requestPermissions();
        microphoneDevices = await deviceService.getAudioInputDevices();
      }
      logger.info(`Permission state: ${permission}`);

      setPermissionState(permission);

      if (permission !== 'granted') {
        logger.warn('Microphone permission not granted, device labels may be unavailable');
      }

      logger.debug('Enumerating audio devices...');
      const speakerDevices = await deviceService.getAudioOutputDevices();

      logger.info('🎤 Microphones found:', {
        count: microphoneDevices.length,
        devices: microphoneDevices.map(d => ({
          id: d.deviceId,
          label: d.label || '(no label)',
        })),
      });

      logger.info('Speakers found:', {
        count: speakerDevices.length,
        devices: speakerDevices.map(d => ({
          id: d.deviceId,
          label: d.label || '(no label)',
        })),
      });

      setMicrophones(microphoneDevices);
      setSpeakers(speakerDevices);
      setRingers(speakerDevices);

      // Load persisted preferences (shared with legacy PhoneStore via localforage)
      const [persistedMic, persistedSpeaker, persistedRinger] =
        await Promise.all([
          getPersistedMicrophone(),
          getPersistedSpeaker(),
          getPersistedRinger(),
        ]);

      logger.debug('📦 Loaded persisted preferences', {
        persistedMic,
        persistedSpeaker,
        persistedRinger,
      });

      // Set microphone - use persisted if available and device exists, else use first
      if (microphoneDevices.length > 0) {
        const micDeviceExists =
          persistedMic &&
          microphoneDevices.some((m) => m.deviceId === persistedMic);
        const selectedMic = micDeviceExists
          ? persistedMic
          : microphoneDevices[0].deviceId;
        const selectedMicLabel =
          microphoneDevices.find((m) => m.deviceId === selectedMic)?.label ||
          selectedMic;

        logger.debug(
          `Setting microphone: ${selectedMicLabel} (persisted: ${String(micDeviceExists)})`
        );
        setSelectedMicrophone(selectedMic);
      }

      // Set speaker - use persisted if available and device exists, else use first
      if (speakerDevices.length > 0) {
        const speakerDeviceExists =
          persistedSpeaker &&
          speakerDevices.some((s) => s.deviceId === persistedSpeaker);
        const selectedSpeaker = speakerDeviceExists
          ? persistedSpeaker
          : speakerDevices[0].deviceId;
        const selectedSpeakerLabel =
          speakerDevices.find((s) => s.deviceId === selectedSpeaker)?.label ||
          selectedSpeaker;

        logger.debug(
          `🔊 Setting speaker: ${selectedSpeakerLabel} (persisted: ${String(speakerDeviceExists)})`
        );
        setSelectedSpeaker(selectedSpeaker);

        // Apply speaker via AudioService
        try {
          await audioService.setSpeaker(selectedSpeaker);
          logger.debug('✅ Speaker applied via AudioService');
        } catch (error) {
          logger.error(
            'Failed to apply speaker',
            error instanceof Error ? error : new Error(String(error))
          );
        }
      }

      // Set ringer - use persisted if available and device exists, else fallback to speaker or first
      if (speakerDevices.length > 0) {
        const ringerDeviceExists =
          persistedRinger &&
          speakerDevices.some((s) => s.deviceId === persistedRinger);
        // Fallback: use persisted speaker, then first device
        const fallbackRinger = persistedSpeaker || speakerDevices[0].deviceId;
        const selectedRinger = ringerDeviceExists
          ? persistedRinger
          : fallbackRinger;
        const selectedRingerLabel =
          speakerDevices.find((s) => s.deviceId === selectedRinger)?.label ||
          selectedRinger;

        logger.debug(
          `Setting ringer: ${selectedRingerLabel} (persisted: ${String(ringerDeviceExists)})`
        );
        setSelectedRinger(selectedRinger);

        try {
          await audioService.setRinger(selectedRinger);
          logger.debug('Ringer applied via AudioService');
        } catch (error) {
          logger.error(
            'Failed to apply ringer',
            error instanceof Error ? error : new Error(String(error))
          );
        }
      }

      logger.info('Devices refreshed successfully', {
        microphones: microphoneDevices.length,
        speakers: speakerDevices.length,
      });
    } catch (error) {
      logger.error(
        'Failed to refresh devices',
        error instanceof Error ? error : new Error(String(error))
      );
    }
  }, [audioService, deviceService, setMicrophones, setSelectedMicrophone, setSelectedSpeaker, setSelectedRinger, setSpeakers, setRingers, setPermissionState]);

  return {
    microphones,
    speakers,
    selectedMicrophone,
    selectedSpeaker,
    selectedRinger,
    selectMicrophone,
    selectSpeaker,
    selectRinger,
    refreshDevices,
    hasMicrophones: microphones.length > 0,
    hasSpeakers: speakers.length > 0,
    hasSelectedMicrophone: selectedMicrophone !== null,
    hasSelectedSpeaker: selectedSpeaker !== null,
  };
};
